import api from "@/api";
import {
  TIMEOUT_CLIENTE_MS,
  TIMEOUT_SSR_MS,
  statusDoErroDaApi,
} from "@/utils/ensaioSSR";

/**
 * Busca das abas do ensaio com SSR.
 *
 * - Metadados (`v3/modelos/{url}`): decidem o status da página. 404 só quando a
 *   API respondeu 404; timeout, 5xx e rede viram 503.
 * - Conteúdo público da aba: buscado **só no servidor** e reduzido a uma lista
 *   permitida (`utils/publicModelPayload.ts`), porque vai para o __NUXT_DATA__.
 *   Falha vira `null`, e a página sai só com a casca, completada pelo cliente.
 * - Busca do cliente: sempre pelo `api` (`withCredentials`). É a resposta dela
 *   que traz o crachá do middleware `cracha.video`; trocar por `$fetch` sem
 *   `credentials: 'include'` faria o assinante receber 403 no vídeo.
 *
 * Chame tudo no setup da página: as URLs saem da rota atual.
 */
export function useEnsaioRota() {
  const route = useRoute();
  const nome = String(route.params.name);
  const idioma = String(route.params.language ?? "");
  const preview = route.query.preview == "true";
  return {
    nome,
    idioma,
    query: `language=${idioma}${preview ? "&preview=true" : ""}`,
    // ensaio não publicado (`?preview=true`) não divide cache com o publicado
    chave: (base: string) => `${base}-${nome}-${idioma}${preview ? "-preview" : ""}`,
  };
}

export function useEnsaioMetadados() {
  const { nome, query, chave } = useEnsaioRota();
  // Chave própria, a mesma em todas as abas: `model-{nome}-{idioma}` já teve
  // outro handler, e com a mesma chave o Nuxt reaproveitaria o da página anterior.
  return useAsyncData(chave("model-info"), async () => {
    try {
      const { data } = await api.get(`v3/modelos/${nome}?${query}`, {
        timeout: import.meta.server ? TIMEOUT_SSR_MS : TIMEOUT_CLIENTE_MS,
        semRedirect404: true,
      });
      return data;
    } catch (erro) {
      const statusCode = statusDoErroDaApi(erro);
      throw createError({
        statusCode,
        statusMessage: statusCode === 404 ? "Not Found" : "Service Unavailable",
      });
    }
  });
}

/**
 * Lança o erro da página quando os metadados não vieram. `fatal` para a página
 * de erro aparecer também na navegação pelo cliente.
 */
export function exigirMetadados(erro: unknown, dados: unknown) {
  if (!erro && dados) return;
  const statusCode = erro ? statusDoErroDaApi(erro) : 503;
  throw createError({
    statusCode,
    statusMessage: statusCode === 404 ? "Not Found" : "Service Unavailable",
    fatal: true,
  });
}

/**
 * Conteúdo público de uma aba para o SSR. No cliente devolve `null` (navegação
 * entre páginas cai no fluxo de cliente); com o interruptor `ssrPiloto`
 * desligado, também.
 */
export function useEnsaioPublico<T>(
  base: string,
  caminho: string,
  montar: (raw: unknown) => T | null,
) {
  const { chave, query } = useEnsaioRota();
  const ligado = useRuntimeConfig().public.ssrPiloto !== false;
  return useAsyncData<T | null>(
    chave(base),
    async () => {
      if (import.meta.client || !ligado) return null;
      try {
        const { data } = await api.get(`${caminho}?${query}`, {
          timeout: TIMEOUT_SSR_MS,
        });
        return montar(data);
      } catch {
        return null;
      }
    },
    { default: () => null },
  );
}

/** Busca do cliente. Falha (inclusive 404) devolve `null`: a página fica com o SSR. */
export async function buscarEnsaioNoCliente(url: string): Promise<unknown> {
  try {
    const { data } = await api.get(url, {
      semRedirect404: true,
      timeout: TIMEOUT_CLIENTE_MS,
    });
    return data;
  } catch {
    return null;
  }
}
